import { useEffect, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import { getProjectsRelatedInventory, RelatedInventoryRiskItem } from '../Api/Projects/projectsApi';
import { normalizeInventoryAsset } from './Inventory';
import { InventoryAsset } from '../types';
import { accountScopedRead } from './accountScopedRead';

export interface RelatedInventoryRow extends RelatedInventoryRiskItem {
  rawStatus?: string | null;
}

// Null until the first fetch resolves, so an empty account genuinely showing no rows is
// distinguishable from "not asked yet" and the grid doesn't flash empty before loading.
const subject = new BehaviorSubject<RelatedInventoryRow[] | null>(null);
const loadingSubject = new BehaviorSubject<boolean>(false);

export const getProjectsRelatedInventorySubject = (): BehaviorSubject<RelatedInventoryRow[] | null> => subject;
export const getProjectsRelatedInventoryLoadingSubject = (): BehaviorSubject<boolean> => loadingSubject;

// Rows arrive raw from the API; run them through the same normalization the inventory list uses
// so client-derived fields (e.g. productDisplay) exist for the shared column defs. `status` is rewritten
// for display, so the raw value is kept for tab partitioning.
export const normalizeRiskItems = (items: RelatedInventoryRiskItem[]): RelatedInventoryRow[] =>
  items.map((item) => ({
    ...(normalizeInventoryAsset(item as InventoryAsset) as RelatedInventoryRow),
    rawStatus: item.status
  }));

// Patch just one row's custom_fields so an inline grid edit survives a remount without a refetch.
export const setProjectsRelatedInventoryCustomFields = (
  assetId: string,
  customFields: RelatedInventoryRiskItem['custom_fields']
): void =>
  subject.next(
    subject.getValue()?.map((item) => (item.id === assetId ? { ...item, custom_fields: customFields } : item)) ?? null
  );

// A failed load (e.g. a 404 from an older backend) leaves the grid empty — no global banner.
const reads = accountScopedRead({
  subject,
  request: getProjectsRelatedInventory,
  empty: [],
  toValue: normalizeRiskItems
});

export const refreshProjectsRelatedInventory = reads.refresh;
export const refreshProjectsRelatedInventoryIfRunning = reads.refreshIfRunning;

export interface UseProjectsRelatedInventoryReturn {
  relatedInventory: RelatedInventoryRow[] | null;
  loading: boolean;
  fetchProjectsRelatedInventory: (forceRefresh?: boolean) => Promise<void>;
}

export const useProjectsRelatedInventory = (): UseProjectsRelatedInventoryReturn => {
  const [relatedInventory, setRelatedInventory] = useState<RelatedInventoryRow[] | null>(subject.getValue());
  const [loading, setLoading] = useState<boolean>(loadingSubject.getValue());

  useEffect(() => {
    const inventorySub = subject.subscribe(setRelatedInventory);
    const loadingSub = loadingSubject.subscribe(setLoading);
    return () => {
      inventorySub.unsubscribe();
      loadingSub.unsubscribe();
    };
  }, []);

  // Every path that finishes settles on an array, so null keeps meaning "never fetched" and nothing
  // else. Callers read that as still-loading; leaving null on a failure would spin forever.
  const fetchProjectsRelatedInventory = async (forceRefresh = false): Promise<void> => {
    if (loadingSubject.getValue()) return;
    if (subject.getValue() !== null && !forceRefresh) return;

    loadingSubject.next(true);
    try {
      await reads.load();
    } finally {
      loadingSubject.next(false);
    }
  };

  return { relatedInventory, loading, fetchProjectsRelatedInventory };
};
